import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, Globe, Instagram, MessageCircle, Ticket, Sparkles, Crown, Copy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { CATEGORY_LABELS } from "../components/FeedCard";
import SafeImage from "../components/SafeImage";
import AiPhotoBadge from "../components/AiPhotoBadge";
import SEO from "../components/SEO";
import ClaimBusiness from "../components/ClaimBusiness";
import Breadcrumbs from "../components/Breadcrumbs";
import { trackEvent, EV } from "../lib/analytics";

export default function BusinessDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/businesses/${slug}`).then(({ data }) => { setB(data); trackEvent(EV.BUSINESS_VIEW, { slug: data.slug, category: data.category }); }).catch(() => setB(null)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="py-20 text-center text-muted-foreground">Carregando...</div>;
  if (!b) return (
    <div className="py-20 text-center text-muted-foreground" data-testid="business-notfound">
      <SEO title="Empresa não encontrada" path={`/empresa/${slug}`} noindex />
      Empresa não encontrada.
    </div>
  );

  const wa = b.whatsapp ? `https://wa.me/${b.whatsapp}?text=${encodeURIComponent("Olá! Vim pelo Guia de Jampa AI.")}` : null;
  const seoDesc = (b.description && b.description.trim().length > 40)
    ? b.description.trim().slice(0, 158)
    : `${b.name}: ${(CATEGORY_LABELS[b.category] || b.category || "empresa").toLowerCase()} em ${b.neighborhood ? `${b.neighborhood}, ` : ""}João Pessoa/PB. Veja endereço, horário, WhatsApp e avaliações no Guia de Jampa.`.slice(0, 160);


  return (
    <div className="space-y-6" data-testid="business-detail">
      <SEO
        title={`${b.name} — ${CATEGORY_LABELS[b.category] || b.category} em João Pessoa`}
        description={seoDesc}
        path={`/empresa/${b.slug}`}
        image={b.cover}
        jsonLd={{
          "@context": "https://schema.org", "@type": "LocalBusiness",
          name: b.name, description: seoDesc, image: b.cover || b.logo,
          ...(b.rating && b.review_count ? { aggregateRating: { "@type": "AggregateRating", ratingValue: b.rating, reviewCount: b.review_count } } : {}),
          ...(b.hours ? { openingHours: b.hours } : {}),
          ...([b.instagram, b.website].filter(Boolean).length ? { sameAs: [b.instagram, b.website].filter(Boolean) } : {}),
          address: { "@type": "PostalAddress", streetAddress: b.address, addressLocality: "João Pessoa", addressRegion: "PB", addressCountry: "BR" },
          telephone: b.whatsapp ? `+${b.whatsapp}` : undefined,
          url: `https://guiadejampa.com/empresa/${b.slug}`,
          ...(b.lat && b.lng ? { geo: { "@type": "GeoCoordinates", latitude: b.lat, longitude: b.lng } } : {}),
        }}
      />
      <button data-testid="business-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <Breadcrumbs items={[
        { name: "Empresas", to: "/empresas" },
        { name: CATEGORY_LABELS[b.category] || b.category, to: `/empresas?category=${b.category}` },
        { name: b.name.length > 60 ? b.name.slice(0, 57) + "…" : b.name },
      ]} />

      <div className="relative -mx-4 md:mx-0 md:rounded-3xl overflow-hidden h-56 md:h-72">
        {b.cover ? (
          <SafeImage src={b.cover} category={b.category} alt={b.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/30 to-secondary" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        {b.cover && b.ai_image && <AiPhotoBadge className="!top-3 !right-3 !bottom-auto !left-auto" />}
        <div className="absolute bottom-4 left-4 right-4 text-white">
          {b.plan === "premium" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[11px] font-bold mb-2"><Crown className="w-3 h-3" />Premium</span>
          )}
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl">{b.name}</h1>
          <p className="text-sm text-white/80">{CATEGORY_LABELS[b.category] || b.category} · João Pessoa</p>
        </div>
      </div>

      <ClaimBusiness business={b} />

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-3">
        {wa && (
          <a data-testid="business-whatsapp" href={wa} target="_blank" rel="noreferrer"
            className="flex flex-col items-center gap-1.5 rounded-2xl border border-border bg-card py-3 active:scale-95 transition-transform">
            <MessageCircle className="w-5 h-5 text-success" /><span className="text-xs font-medium">WhatsApp</span>
          </a>
        )}
        {b.instagram && (
          <a data-testid="business-instagram" href={`https://instagram.com/${b.instagram}`} target="_blank" rel="noreferrer"
            className="flex flex-col items-center gap-1.5 rounded-2xl border border-border bg-card py-3 active:scale-95 transition-transform">
            <Instagram className="w-5 h-5 text-primary" /><span className="text-xs font-medium">Instagram</span>
          </a>
        )}
        {b.website && (
          <a data-testid="business-website" href={b.website} target="_blank" rel="noreferrer"
            className="flex flex-col items-center gap-1.5 rounded-2xl border border-border bg-card py-3 active:scale-95 transition-transform">
            <Globe className="w-5 h-5 text-primary" /><span className="text-xs font-medium">Site</span>
          </a>
        )}
      </div>

      <div className="rounded-3xl border border-border bg-card p-5 space-y-3">
        <p className="text-sm leading-relaxed text-muted-foreground">{b.description}</p>
        <div className="flex items-start gap-2 text-sm"><MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" /><span>{b.address}</span></div>
        <div className="flex items-center gap-2 text-sm"><Clock className="w-4 h-4 text-primary shrink-0" /><span>{b.hours}</span></div>
      </div>

      {/* Photos */}
      {b.photos?.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-lg mb-3">Fotos</h2>
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
            {b.photos.map((ph, i) => (
              <SafeImage key={ph} src={ph} category={b.category} alt={`${b.name} ${i + 1}`} loading="lazy" className="w-56 h-40 rounded-2xl object-cover shrink-0" />
            ))}
          </div>
        </div>
      )}

      {/* Coupons */}
      {b.coupons?.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Ticket className="w-5 h-5 text-primary" />Cupons</h2>
          <div className="space-y-3">
            {b.coupons.map((c) => (
              <div key={c.id} data-testid={`coupon-${c.id}`} className="flex items-center justify-between rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-4">
                <div>
                  <p className="font-heading font-bold">{c.title}</p>
                  <p className="text-xs text-muted-foreground">{c.description}</p>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(c.code); toast.success(`Cupom ${c.code} copiado!`); }}
                  className="flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-2 text-sm font-bold active:scale-95 transition-transform">
                  <Copy className="w-3.5 h-3.5" /> {c.code}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button data-testid="business-ask-ai" onClick={() => navigate(`/ia?p=${encodeURIComponent(b.name)}`)}
        className="w-full flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground py-3.5 font-semibold active:scale-[0.98] transition-transform">
        <Sparkles className="w-4 h-4" /> Perguntar à IA sobre {b.name}
      </button>
    </div>
  );
}
